import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { deviceQueryKeys } from "./deviceQueryKeys";
import { getQueryKey as getAllDevicesByOrgKey } from "./useAllDevicesByOrg";

interface DeleteDeviceVariables {
  deviceId: string;
  /**
   * The device's owning org. It is passed in rather than read from the
   * current-org context because the 204 response carries no body to recover it
   * from, and the success handler needs it to scope cache invalidation.
   */
  orgId: string;
}

export function useDeleteDevice() {
  const { devices } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<void, Error, DeleteDeviceVariables>({
    mutationFn: ({ deviceId, orgId }) =>
      devices.deleteDevice(deviceId, { resourceOwnerId: orgId }),
    onSuccess: (_data, { deviceId, orgId }) => {
      // A device is gone, so every per-org listing of devices — the grid and
      // the all-devices roster behind device pickers — is now stale.
      void queryClient.invalidateQueries({
        queryKey: deviceQueryKeys.lists(orgId),
      });

      void queryClient.invalidateQueries({
        queryKey: getAllDevicesByOrgKey(orgId),
      });

      // Drop the detail record and the device's tokens rather than
      // invalidating them: refetching either would only 404, since deleting a
      // device deletes its tokens too.
      queryClient.removeQueries({ queryKey: ["device", deviceId] });
      queryClient.removeQueries({ queryKey: ["deviceTokens", deviceId] });
    },
  });
}
